import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { formatDateOrdinal } from '../utils/dateUtils';
import TrophyWidget from '../components/TrophyWidget';
import { renderKitPreview, getKitStyleById } from '../utils/kitUtils';
import MyPredictionsSummary from '../components/MyPredictionsSummary';
import LatestPredictions from '../components/LatestPredictions';

interface UserProfile {
  id: string;
  teamName: string;
  createdAt: string;
  kitStyle?: string;
  kitPrimaryColour?: string;
  kitSecondaryColour?: string;
  isActive: boolean;
}

interface GlobalRank {
  rank: number | null;
  hasRank: boolean;
  isUnranked: boolean;
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [globalRank, setGlobalRank] = useState<GlobalRank | null>(null);
  const [loading, setLoading] = useState(true);
  const [rankLoading, setRankLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/users/profile/${userId}`);
        setUserProfile(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  // Separate effect to fetch global rank
  useEffect(() => {
    const fetchGlobalRank = async () => {
      if (!userId) return;
      
      try {
        setRankLoading(true);
        const response = await apiClient.get(`/users/${userId}/global-rank`);
        setGlobalRank(response.data);
      } catch (error) {
        console.error('Failed to fetch global rank:', error);
        setGlobalRank(null);
      } finally {
        setRankLoading(false);
      }
    };

    fetchGlobalRank();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading user profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <Link 
            to="/" 
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl mb-4">User not found</div>
          <Link 
            to="/" 
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === userProfile.id;

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link 
              to="/" 
              className="inline-block" 
              style={{ color: 'var(--highlight)' }}
            >
              ← Back to Home
            </Link>
            {isOwnProfile && (
              <Link
                to="/profile"
                className="btn-secondary px-4 py-2 rounded-md"
              >
                Edit Profile
              </Link>
            )}
          </div>

          {/* User Info Card */}
          <div className="card rounded-lg shadow p-6 bg-gray-50 dark:bg-gray-800/30">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              
              {/* Kit Display */}
              <div className="flex-shrink-0">
                {userProfile.kitStyle ? (
                  <div className="w-24 h-24 flex items-center justify-center rounded-lg">
                    {(() => {
                      const kitStyle = getKitStyleById(userProfile.kitStyle);
                      if (kitStyle) {
                        return renderKitPreview(
                          kitStyle,
                          userProfile.kitPrimaryColour || '#2563eb',
                          userProfile.kitSecondaryColour || '#ffffff',
                          'large'
                        );
                      }
                      return (
                        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-2xl font-bold" style={{ color: 'var(--text-secondary)' }}>
                            {userProfile.teamName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center shadow-sm">
                    <span className="text-3xl font-bold" style={{ color: 'var(--text-secondary)' }}>
                      {userProfile.teamName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* User Details */}
              <div className="text-center sm:text-left">
                <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  {userProfile.teamName}
                  {isOwnProfile && (
                    <span className="ml-3 text-sm bg-highlight text-black px-3 py-1 rounded-full font-medium">
                      Your Profile
                    </span>
                  )}
                </h1>


                {/* Global Rank */}
                <div className="flex items-center gap-2 mb-2 mt-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                                     {rankLoading ? (
                     <span>Loading rank...</span>
                   ) : globalRank?.hasRank ? (
                     <Link to="/leaderboards" className="hover:underline" style={{ color: 'var(--highlight)' }}>
                       Global Rank #{globalRank.rank}
                     </Link>
                   ) : globalRank?.isUnranked ? (
                     <Link to="/leaderboards" className="hover:underline" style={{ color: 'var(--text-secondary)' }}>
                       Global Rank: Unranked (Outside Top 1000)
                     </Link>
                   ) : globalRank && !globalRank.hasRank ? (
                     <Link to="/leaderboards" className="hover:underline" style={{ color: 'var(--text-secondary)' }}>
                       No rank yet
                     </Link>
                   ) : null}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Playing since {formatDateOrdinal(userProfile.createdAt)}</span>
                  </div>
                  


                </div>
              </div>
            </div>
          </div>
        </div>



        {/* Predictions Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Prediction Statistics
          </h2>
          <MyPredictionsSummary userId={userProfile.id} />
        </div>

        {/* Latest Predictions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Latest Predictions
          </h2>
          <LatestPredictions userId={userProfile.id} limit={20} />
        </div>


        {/* Trophy Widget */}
        <div className="mb-8">
        <TrophyWidget userId={userProfile.id} />
        </div>



        {/* Additional Info for Own Profile */}
        {isOwnProfile && (
          <div className="card rounded-lg shadow p-6 bg-blue-50 dark:bg-blue-900/20">
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Profile Sharing
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Your profile is publicly accessible. You can share this URL with others to show them your prediction statistics.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={window.location.href}
                readOnly
                className="flex-1 px-3 py-2 border rounded text-sm"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)'
                }}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="btn-secondary px-4 py-2 text-sm"
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;